import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarClock,
  CalendarPlus,
  CalendarX2,
  CheckCircle2,
  Download,
  ExternalLink,
  FileCode2,
  FileCheck2,
  Link2,
  MessageSquare,
  PackageCheck,
  Printer,
  RefreshCw,
  Send,
  Truck,
  XCircle,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { cn, nomeFornecedor } from '@/lib/utils';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { Gaveta } from '@/componentes/ui/gaveta';
import { Campo, Esqueleto, Pilula } from '@/componentes/ui/primitivos';
import { Seletor } from '@/componentes/ui/seletor';
import { EstadoErro } from '@/componentes/ui/estados';
import { agruparDivergencias, contaDaQuantidade, GRUPOS_DIVERGENCIA } from '@wms/shared';
import { moeda, type DeParaPendente, type DetalheNota, type Destino, type Evento, precosDaMensagem, SUBETAPAS_RECEBIMENTO, TIPO_DIVERGENCIA } from './tipos';
import { DispensarNota } from './Dispensar';
import { Roteiro } from './Roteiro';
import { CampoData, isoDaqui } from './GavetaAgendar';
import { TransferenciaNf, useRastroTransferencia } from './TransferenciaNf';

/**
 * A nota aberta. A regra desta gaveta: UMA ação principal, a da etapa, e só se
 * a pessoa puder fazê-la. Quem não pode vê o que falta e de quem é a vez — e
 * pode conversar no Slack com quem age, que é a ação que vale para todos.
 */
export function GavetaNota({
  chave,
  aoFechar,
  aoAgendar,
}: {
  chave: string | null;
  aoFechar: () => void;
  aoAgendar: (chaves: string[]) => void;
}): JSX.Element {
  const consulta = useQuery<DetalheNota>({
    queryKey: ['entrada', chave],
    queryFn: () => api.get<DetalheNota>(`/api/entradas/nota/${chave}`),
    enabled: Boolean(chave),
    // Gaveta aberta: confere a cada 30 s (o GET também lê respostas novas na thread do Slack).
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const d = consulta.data;

  return (
    <Gaveta
      aberta={Boolean(chave)}
      aoFechar={aoFechar}
      // 45% da tela (pedido do Paulo, 27/10/2026: os 480px fixos ficavam em ~25% num monitor de 1920).
      className="sm:w-[min(100vw,max(480px,45vw))]"
      titulo={d ? (nomeFornecedor(d.nota.emitenteNome, d.nota.emitenteUf) ?? d.nota.emitenteCnpj) : 'Nota'}
      subtitulo={
        d
          ? `NF ${d.nota.numero ?? '—'}/${d.nota.serie ?? ''} · ${moeda(d.nota.valor)}${d.nota.emissao ? ` · emitida ${new Date(d.nota.emissao).toLocaleDateString('pt-BR')}` : ''}`
          : undefined
      }
    >
      {consulta.isLoading ? (
        <div className="space-y-3 p-5">
          <Esqueleto className="h-20" />
          <Esqueleto className="h-32" />
        </div>
      ) : consulta.isError ? (
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : d ? (
        <div className="space-y-5 p-5">
          {/* Topo da gaveta: DANFE/XML à mão, os dados que identificam a nota (o cabeçalho), a etapa e o roteiro. */}
          <Documentos d={d} />
          {/* A etapa entra no cabeçalho; a explicação e o "há X h" já estão no passo atual do roteiro. */}
          <Fatos d={d} />

          <Roteiro
            d={d}
            deParaAberto={(modo) => <ConfirmarDePara d={d} modo={modo} abertoInicial />}
            aoAgendar={() => aoAgendar([d.nota.chave])}
            irParaAcoes={() => document.getElementById(`acoes-${d.nota.chave}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          />
          {/* Item não achado: o de-para É a pendência, em destaque. Achado pelo EAN: só cadastro, mais abaixo. */}
          <EntrouComDivergencia d={d} />
          <TransferenciaNf d={d} />
          <FaltaAlocar d={d} />
          <Acao d={d} aoAgendar={aoAgendar} />
          <DispensarNota chave={d.nota.chave} podeDispensar={d.podeDispensar} podeReincluir={d.podeReincluir} motivoAtual={d.nota.motivo} />
          <Conversa d={d} />
          <Itens d={d} />
          <LinhaDoTempo chave={d.nota.chave} eventos={d.linhaDoTempo} atualizando={consulta.isFetching} aoAtualizar={() => void consulta.refetch()} />
        </div>
      ) : null}
    </Gaveta>
  );
}

/**
 * Código do fornecedor sem de-para no ERP: pergunta "IMPHP0068 é o SKU 31253?"
 * (o SKU que o EAN achou). Confirmado, o WMS grava o de-para e revalida a nota;
 * a linha do tempo registra quem confirmou (pedido do Paulo, 30/10/2026).
 */
function ConfirmarDePara({ d, modo, abertoInicial }: { d: DetalheNota; modo: 'pendencia' | 'cadastro'; abertoInicial?: boolean }): JSX.Element | null {
  // pendencia: o item não foi achado (sem SKU) e trava a nota. cadastro: achado pelo EAN, o vínculo é só seguro.
  const pendentes = (d.deParas ?? []).filter((p) => (modo === 'pendencia' ? p.produto == null : p.produto != null));
  const [aberto, definirAberto] = React.useState(Boolean(abertoInicial));
  if (!d.podeDePara || !pendentes.length) return null;
  const pendencia = modo === 'pendencia';
  // Cadastro: uma linha só, no alto; a comparação abre no clique (não compete com a pendência da nota).
  if (!pendencia && !aberto) {
    const p = pendentes[0]!;
    return (
      <button
        type="button"
        onClick={() => definirAberto(true)}
        // Borda azul com faixa à esquerda: separa este pedido de cadastro dos fatos e documentos ao redor.
        className="flex w-full items-center gap-2 rounded-lg border border-primaria/40 border-l-4 border-l-primaria bg-primaria-suave/30 px-3 py-2 text-left text-xs shadow-xs transition hover:border-primaria/70 hover:bg-primaria-suave/60"
      >
        <Link2 className="h-3.5 w-3.5 shrink-0 text-primaria" />
        <span className="min-w-0 flex-1 truncate text-textoFraco">
          {/* É sugestão (pelo EAN), não afirmação: quem confirma é o usuário. */}
          <b className="text-texto">Cadastro:</b> o código <b className="font-mono text-texto">{p.cprod}</b> é o{' '}
          <b className="text-texto">SKU {p.produto}</b>? <span className="text-textoSuave">Sugestão pelo EAN, sem de-para no ERP</span>
          {pendentes.length > 1 ? ` (e mais ${pendentes.length - 1})` : ''}
        </span>
        <span className="shrink-0 font-semibold text-primaria">Conferir</span>
      </button>
    );
  }
  return (
    <section
      className={cn(
        'space-y-2.5 rounded-xl border p-4',
        pendencia ? 'border-status-atencao/40 bg-status-atencaoFundo/40' : 'border-borda bg-superficie2/40',
      )}
    >
      <div>
        <p className="flex items-center gap-2 text-[13px] font-semibold text-texto">
          <Link2 className={cn('h-4 w-4', pendencia ? 'text-status-atencao' : 'text-textoSuave')} />
          {pendencia
            ? pendentes.length === 1
              ? 'Produto não encontrado: diga qual é o SKU'
              : `${pendentes.length} produtos não encontrados: diga qual é o SKU`
            : 'Cadastro: ligar o código do fornecedor ao SKU'}
        </p>
        <p className="mt-0.5 text-xs text-textoFraco">
          {pendencia
            ? 'O código do fornecedor não está ligado a um SKU no ERP e o EAN da nota não achou o produto. Sem isso a nota não segue.'
            : 'Não trava a nota: o item já foi achado pelo EAN. Com o vínculo, ele continua reconhecido se o EAN faltar no XML.'}
        </p>
      </div>
      <ul className="space-y-2">
        {pendentes.map((p) => (
          <ItemDePara key={p.nItem} chave={d.nota.chave} fornecedor={d.nota.emitenteNome} p={p} />
        ))}
      </ul>
    </section>
  );
}

function ItemDePara({ chave, fornecedor, p }: { chave: string; fornecedor: string | null; p: DeParaPendente }): JSX.Element {
  const cliente = useQueryClient();
  const [outro, definirOutro] = React.useState(p.produto == null);
  const [sku, definirSku] = React.useState('');
  const [fator, definirFator] = React.useState('1');
  const skuNum = Number(sku);
  const busca = useQuery<{ produto: number; descricao: string | null }>({
    queryKey: ['entrada-produto', skuNum],
    queryFn: () => api.get(`/api/entradas/produto/${skuNum}`),
    enabled: outro && Number.isInteger(skuNum) && skuNum > 0,
    retry: false,
  });
  const alvo = outro ? (busca.data ? busca.data.produto : null) : p.produto;
  const gravar = useMutation({
    mutationFn: () => api.post<{ status: string }>(`/api/entradas/nota/${chave}/de-para`, { nItem: p.nItem, produto: alvo, fator: Number(fator) || 1 }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['entrada', chave] });
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
    },
  });
  const desc = (p.descricao ?? '').replace(/\s+(LOTE|EAN)[:\s].*$/i, '');
  const [mudarEmbalagem, definirMudarEmbalagem] = React.useState(false);
  const un = p.unidade ?? 'un.';
  return (
    <li className="space-y-3 rounded-lg border border-borda bg-superficie p-3 text-xs">
      {/* Lado a lado: o que veio na nota × o produto do ERP. A pergunta é se os dois são o mesmo. */}
      <div className="grid items-stretch gap-2 sm:grid-cols-[1fr_auto_1fr]">
        <div className="rounded-md bg-superficie2 px-3 py-2">
          <p className="text-2xs font-semibold uppercase tracking-wide text-textoSuave">Na nota{fornecedor ? ` · ${fornecedor.split(' ')[0]}` : ''}</p>
          <p className="mt-0.5 font-mono text-[13px] font-bold text-texto">{p.cprod}</p>
          <p className="text-texto">{desc || '—'}</p>
          <p className="text-textoSuave">
            {p.quantidade != null ? `${p.quantidade.toLocaleString('pt-BR')} ${un}` : ''}
            {p.ean ? ` · EAN ${p.ean}` : ''}
          </p>
        </div>
        <span className="hidden self-center text-lg text-textoSuave sm:block">→</span>
        <div className={cn('rounded-md px-3 py-2', alvo != null ? 'bg-primaria-suave/50' : 'border border-dashed border-borda')}>
          <p className="text-2xs font-semibold uppercase tracking-wide text-textoSuave">{outro ? 'SKU no ERP' : 'Sugestão do WMS'}</p>
          {outro ? (
            <div className="mt-1 space-y-1">
              <Campo
                inputMode="numeric"
                placeholder="Digite o SKU, ex.: 31253"
                value={sku}
                onChange={(e) => definirSku(e.target.value.replace(/\D/g, ''))}
              />
              {busca.data ? (
                <p className="text-texto">{busca.data.descricao ?? 'sem descrição'}</p>
              ) : busca.isError ? (
                <p className="text-status-divergencia">SKU não encontrado no ERP.</p>
              ) : null}
            </div>
          ) : (
            <>
              <p className="mt-0.5 text-[13px] font-bold text-texto">SKU {p.produto}</p>
              <p className="text-texto">{p.descricaoProduto ?? '—'}</p>
              {p.ean ? <p className="text-textoSuave">pelo EAN {p.ean}: confira se é o mesmo produto</p> : null}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-[13px] font-semibold text-texto">{outro ? 'Ligar a este SKU?' : 'É o mesmo produto?'}</span>
        <Botao variante="primaria" tamanho="pequeno" disabled={alvo == null || !(Number(fator) > 0)} carregando={gravar.isPending} onClick={() => gravar.mutate()}>
          <CheckCircle2 className="h-4 w-4" /> {outro ? `Sim, ligar ao SKU ${alvo ?? '…'}` : 'Sim, é o mesmo'}
        </Botao>
        {p.produto != null ? (
          <Botao variante="secundaria" tamanho="pequeno" onClick={() => definirOutro(!outro)}>
            {outro ? `Voltar ao SKU ${p.produto}` : 'Não, é outro SKU'}
          </Botao>
        ) : null}
      </div>

      <p className="text-textoSuave">
        {mudarEmbalagem ? (
          <span className="inline-flex items-center gap-1.5">
            1 {un} da nota =
            <Campo className="h-7 w-14 text-center" inputMode="numeric" value={fator} onChange={(e) => definirFator(e.target.value.replace(/[^\d]/g, ''))} />
            un. no estoque
          </span>
        ) : (
          <>
            Embalagem: 1 {un} = {fator} un. no estoque ·{' '}
            <button type="button" className={acaoTexto} onClick={() => definirMudarEmbalagem(true)}>
              alterar
            </button>
          </>
        )}
        <span className="block">Confirmando, o WMS grava o vínculo no cadastro do produto e confere a nota de novo. Fica na linha do tempo.</span>
      </p>
      {gravar.error ? (
        <p className="text-status-divergencia">{gravar.error instanceof ErroHttp ? gravar.error.message : String(gravar.error)}</p>
      ) : null}
    </li>
  );
}

/** Atalho para a edição de uma NF de compra: botão (chip com ícone e seta), não número solto que parece texto. Numa linha só. */
function EditarNfCompra({ nf }: { nf: number | string }): JSX.Element {
  return (
    <Link to={`/entradas?aba=nfcompra&nf=${nf}`} title={`Abrir a NF de compra ${nf} para editar`} className={cn(acaoTexto, 'whitespace-nowrap text-xs')}>
      <FileCheck2 className="h-3.5 w-3.5 shrink-0" />
      {nf}
      <span className="font-normal">· editar</span>
      <ArrowRight className="h-3 w-3 shrink-0" />
    </Link>
  );
}

function Fatos({ d }: { d: DetalheNota }): JSX.Element {
  const n = d.nota;
  // O cabeçalho da nota (campos fixos, "—" quando ainda não há), com a etapa à frente.
  const fatos: [string, React.ReactNode][] = [
    ['OC', n.pedidoCompra ? `${n.pedidoCompra}${n.ocOrigem ? ` (${n.ocOrigem === 'xPed' ? 'pela nota' : n.ocOrigem === 'indicada' ? 'indicada' : n.ocOrigem === 'unica' ? 'única aberta do fornecedor' : n.ocOrigem === 'xped_corrigido' ? 'corrigida: a OC do XML não tinha os itens' : n.ocOrigem})` : ''}` : '—'],
    // Com OC: o pré-pedido de onde ela saiu (ou "sem pré-pedido", que também é informação).
    ...(n.pedidoCompra ? ([['Pré-pedido', d.prePedido ?? 'sem pré-pedido']] as [string, React.ReactNode][]) : []),
    ['Comprador', n.compradorNome ?? '—'],
    ['NF de compra (ERP)', n.nfCompra ? <EditarNfCompra nf={n.nfCompra} /> : '—'],
    ['Destino', n.destinoNome ? `${n.destinoNome}${n.agendaData ? ` · ${n.agendaData.split('-').reverse().join('/')}` : ''}` : '—'],
  ];
  if (n.nfCompraDestino) fatos.push(['Entrada da transferência', <EditarNfCompra key="dest" nf={n.nfCompraDestino} />]);
  if (n.recebimento) fatos.push(['Cega', n.recebimento]);
  if (n.dohFuturo != null) fatos.push(['DOH depois de receber', `${n.dohFuturo} dias`]);
  return (
    <div className="space-y-2 rounded-xl border border-borda bg-superficie2 p-3 text-[13px]">
      <Pilula tom={d.etapa.tom}>{d.etapa.rotulo}</Pilula>
      {fatos.length ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
          {fatos.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <dt className="text-2xs font-semibold uppercase tracking-wide text-textoSuave">{k}</dt>
              <dd className="truncate font-medium">{v}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

/**
 * DANFE e XML da nota, do acervo do portal fiscal (o portal desenha a DANFE
 * a partir do XML). "Ver / imprimir" abre o PDF no navegador, que já tem
 * imprimir e salvar; "Baixar" salva direto.
 */
function Documentos({ d }: { d: DetalheNota }): JSX.Element {
  const base = `/api/entradas/nota/${d.nota.chave}/arquivo`;
  const botao = 'inline-flex h-8 items-center gap-1.5 rounded-lg border border-borda bg-superficie px-2.5 text-xs font-semibold hover:border-bordaForte hover:bg-superficie2';
  if (d.nota.resumo) {
    return (
      <p className="rounded-lg bg-superficie2 px-3 py-2 text-xs text-textoFraco">
        DANFE e XML ficam disponíveis quando o portal fiscal baixar o XML completo (a ciência é automática).
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-2xs font-semibold uppercase tracking-wide text-textoSuave">DANFE</span>
      <a className={botao} href={`${base}/pdf`} target="_blank" rel="noopener noreferrer">
        <Printer className="h-3.5 w-3.5" /> Ver / imprimir
      </a>
      <a className={botao} href={`${base}/pdf?baixar=1`}>
        <Download className="h-3.5 w-3.5" /> Baixar
      </a>
      <a className={botao} href={`${base}/xml?baixar=1`}>
        <FileCode2 className="h-3.5 w-3.5" /> XML
      </a>
    </div>
  );
}

// ------------------------------------------------------------------ a ação da etapa

/**
 * "Dar entrada fiscal" com a caixa "Gerar a cega junto": o armazém da empresa
 * da nota já vem escolhido, e dá para trocar antes de gravar. Com o parâmetro
 * ligado no Cadastro, a caixa vem marcada e não desmarca.
 */
function EntradaFiscal({
  d,
  carregando,
  resultado,
  aoDarEntrada,
}: {
  d: DetalheNota;
  carregando: boolean;
  resultado: unknown;
  aoDarEntrada: (corpo: { cega?: boolean; destinoId?: number | null; data?: string }) => void;
}): JSX.Element {
  const n = d.nota;
  const destinos = useQuery<{ destinos: Destino[] }>({ queryKey: ['entradas-destinos'], queryFn: () => api.get('/api/entradas/destinos') });
  const armazens = React.useMemo(
    () => (destinos.data?.destinos ?? []).filter((x) => x.ativo && x.tipo === 'ARMAZEM' && x.empresa === n.empresa && x.centroRecebimento),
    [destinos.data, n.empresa],
  );
  const [marcada, definirMarcada] = React.useState(d.cegaJunto);
  const [destinoId, definirDestino] = React.useState('');
  const [data, definirData] = React.useState(isoDaqui(0));
  React.useEffect(() => {
    if (!destinoId && armazens.length === 1) definirDestino(String(armazens[0]!.id));
  }, [armazens, destinoId]);
  const gerar = d.cegaJunto || marcada;
  const escolhido = armazens.find((x) => String(x.id) === destinoId);
  const erroCega = (resultado as { cega?: { erro?: string } } | undefined)?.cega?.erro;

  return (
    <>
      <label
        className={cn(
          'flex items-start gap-2.5 rounded-lg border px-3 py-2 text-[13px] transition-colors',
          gerar ? 'border-primaria bg-primaria-suave' : 'border-borda hover:bg-superficie2',
          d.cegaJunto ? 'cursor-default' : 'cursor-pointer',
        )}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-[rgb(var(--cor-primaria))]"
          checked={gerar}
          disabled={d.cegaJunto}
          onChange={(e) => definirMarcada(e.target.checked)}
        />
        <span>
          <b>Gerar a cega junto</b>
          {d.cegaJunto ? <span className="text-textoFraco"> · ligado para todas as notas no Cadastro</span> : null}
          <span className="block text-xs text-textoFraco">
            O recebimento sai logo depois da NF de compra e a nota vai direto para Em conferência, sem agendar. O estoque entra agora no centro de
            recebimento.
          </span>
        </span>
      </label>
      {gerar ? (
        destinos.isLoading ? (
          <Esqueleto className="h-9 w-full" />
        ) : armazens.length ? (
          <div className="space-y-1">
            <Seletor
              rotuloAcessivel="Armazém da cega"
              vazio="Escolha o armazém"
              valor={destinoId}
              aoMudar={definirDestino}
              className="w-full"
              opcoes={armazens.map((x) => ({ valor: String(x.id), rotulo: `${x.nome} ${x.uf}`, auxiliar: `empresa ${x.empresa} · centro ${x.centroRecebimento}` }))}
            />
            {escolhido ? (
              <p className="text-xs text-textoFraco">
                Empresa {escolhido.empresa} · centro {escolhido.centroRecebimento}. Outro CNPJ (Luft, Lisa, Full) não entra aqui: lá a cega sai sobre a NF de
                transferência.
              </p>
            ) : null}
            <p className="pt-1 text-xs font-semibold text-textoFraco">Chegada prevista (põe a nota na grade de recebimento)</p>
            <CampoData valor={data} aoMudar={definirData} />
          </div>
        ) : (
          <p className="rounded-lg bg-superficie2 px-3 py-2 text-xs text-textoFraco">
            A empresa {n.empresa} não tem armazém cadastrado: só a entrada sai agora, e a cega na chegada.
          </p>
        )
      ) : null}
      {erroCega ? (
        <p className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-xs text-status-atencao">A NF de compra saiu, mas a cega não: {erroCega}</p>
      ) : null}
      <Botao
        variante="primaria"
        className="w-full"
        disabled={gerar && armazens.length > 0 && !escolhido}
        carregando={carregando}
        onClick={() => aoDarEntrada(gerar && escolhido ? { cega: true, destinoId: escolhido.id, data } : {})}
      >
        <FileCheck2 className="h-4 w-4" /> {gerar && escolhido ? 'Dar entrada fiscal e gerar a cega' : 'Dar entrada fiscal'}
      </Botao>
    </>
  );
}

function useAcao(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ caminho, corpo }: { caminho: string; corpo?: unknown }) => api.post<unknown>(`/api/entradas/nota/${chave}/${caminho}`, corpo ?? {}),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['entrada', chave] });
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
    },
  });
}

function Acao({ d, aoAgendar }: { d: DetalheNota; aoAgendar: (chaves: string[]) => void }): JSX.Element | null {
  const n = d.nota;
  const acao = useAcao(n.chave);
  const [oc, definirOc] = React.useState('');
  const [motivo, definirMotivo] = React.useState('');
  const [recusando, definirRecusando] = React.useState(false);
  const [volumes, definirVolumes] = React.useState('');
  const [nfDestino, definirNfDestino] = React.useState('');
  const transf = useRastroTransferencia(d);
  const destinos = useQuery<{ destinos: Destino[] }>({ queryKey: ['entradas-destinos'], queryFn: () => api.get('/api/entradas/destinos') });
  const destinoFull = destinos.data?.destinos.find((x) => x.id === n.destinoId)?.tipo === 'FULL';

  const erro = acao.error instanceof ErroHttp ? acao.error.message : acao.error ? String(acao.error) : null;
  const rodar = (caminho: string, corpo?: unknown) => acao.mutate({ caminho, corpo });

  const divergencias = d.divergencias.length ? <TabelaDivergencias d={d} /> : null;

  let corpo: React.ReactNode = null;

  if (!d.podeAgir) {
    // Nada a mostrar (concluída, sem divergência, sem dono): sem a caixa "O que falta" vazia.
    corpo =
      divergencias || d.etapa.dono ? (
        <>
          {divergencias}
          {d.etapa.dono ? (
            <p className="text-[13px] text-textoFraco">
              Esta etapa é do <b>{{ comprador: 'comprador', fiscal: 'fiscal', logistica: 'logística', cd: 'CD' }[d.etapa.dono]}</b>. Se
              precisar, chame no Slack logo abaixo.
            </p>
          ) : null}
        </>
      ) : null;
  } else if (n.status === 'sem_oc' || n.status === 'divergente' || n.status === 'recusada') {
    corpo = (
      <>
        {divergencias}
        {n.status === 'recusada' ? (
          <p className="text-[13px] text-textoFraco">Recusada: {n.motivo}. Quando chegar a carta de correção ou a nota nova, revalide.</p>
        ) : null}
        {d.ocsDoFornecedor.length ? (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-textoFraco">
              {n.status === 'sem_oc' ? 'Qual destas OCs abertas do fornecedor é a desta nota?' : 'Era outra OC? As abertas do fornecedor:'}
            </p>
            <ul className="space-y-1.5">
              {d.ocsDoFornecedor.slice(0, 5).map((o) => {
                const cobreTudo = o.itensNota > 0 && o.emComum === o.itensNota;
                return (
                  <li
                    key={o.oc}
                    className={cn(
                      'flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-[13px]',
                      cobreTudo ? 'border-status-livre/40 bg-status-livreFundo/40' : 'border-borda bg-superficie',
                    )}
                  >
                    <span className="min-w-0">
                      <b>OC {o.oc}</b>
                      <span className="text-textoFraco">
                        {' '}
                        · {new Date(`${o.data}T12:00:00`).toLocaleDateString('pt-BR')}
                        {o.comprador ? ` · ${o.comprador}` : ''} · {o.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                      <span className={cn('block text-xs', cobreTudo ? 'font-semibold text-status-livre' : 'text-textoSuave')}>
                        {o.itensNota ? `cobre ${o.emComum} de ${o.itensNota} itens da nota` : `${o.itensOc} itens na OC`}
                      </span>
                    </span>
                    <Botao
                      tamanho="micro"
                      variante={cobreTudo ? 'primaria' : 'secundaria'}
                      disabled={o.oc === n.pedidoCompra}
                      carregando={acao.isPending}
                      onClick={() => rodar('oc', { oc: o.oc })}
                    >
                      {o.oc === n.pedidoCompra ? 'É a atual' : 'É esta'}
                    </Botao>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : n.status === 'sem_oc' ? (
          <p className="text-[13px] text-textoFraco">Nenhuma OC aberta deste fornecedor nos últimos 180 dias, na empresa da nota.</p>
        ) : null}
        <div className="flex gap-2">
          <Campo
            inputMode="numeric"
            placeholder={d.ocsDoFornecedor.length ? 'Não está na lista? Digite o nº da OC' : 'Sabe a OC? Digite o número'}
            value={oc}
            onChange={(e) => definirOc(e.target.value.replace(/\D/g, ''))}
          />
          {oc ? (
            <Botao variante="primaria" carregando={acao.isPending} onClick={() => rodar('oc', { oc })}>
              Usar esta OC
            </Botao>
          ) : (
            <Botao variante="secundaria" carregando={acao.isPending} onClick={() => rodar('revalidar')}>
              <RefreshCw className="h-4 w-4" /> Revalidar
            </Botao>
          )}
        </div>
        <p className="text-xs text-textoSuave">
          Não sabe a OC? Pergunte ao comprador em <b>Conversa no Slack</b>, logo abaixo — se ele responder <code>oc 12345</code> na thread, a
          nota é conferida e vinculada sozinha.
        </p>
        {n.status !== 'recusada' ? (
          // As duas decisões lado a lado: aceitar (erro nosso / combinado) ou recusar (erro do fornecedor).
          <div className="grid gap-2 sm:grid-cols-2">
          {n.status === 'divergente' && d.divergencias.length && !recusando ? <AceitarNaValidacao chave={n.chave} /> : null}
          {recusando ? (
            <div className="space-y-2.5 rounded-lg border border-status-divergencia/40 bg-superficie p-3 sm:col-span-2">
              <p className="flex items-center gap-2 text-[13px] font-semibold text-status-divergencia">
                <XCircle className="h-4 w-4" /> Recusar a nota
              </p>
              <p className="text-xs text-textoFraco">
                A nota para e espera carta de correção, cancelamento ou nota nova do fornecedor. O motivo vai para o fornecedor e para a linha do tempo.
              </p>
              <label className="block space-y-1">
                <span className="text-xs font-semibold text-texto">Motivo</span>
                <Campo placeholder="Ex.: preço acima do combinado, NCM errado" value={motivo} onChange={(e) => definirMotivo(e.target.value)} />
              </label>
              <div className="flex justify-end gap-2">
                <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirRecusando(false)}>
                  Voltar
                </Botao>
                <Botao variante="perigo" tamanho="pequeno" disabled={!motivo.trim()} carregando={acao.isPending} onClick={() => rodar('recusar', { motivo })}>
                  <XCircle className="h-4 w-4" /> Confirmar recusa
                </Botao>
              </div>
            </div>
          ) : (
            <CartaoAcao
              icone={XCircle}
              tom="perigo"
              titulo="Recusar a nota"
              ajuda="O erro é do fornecedor (preço, quantidade, imposto): pede correção ou nota nova"
              aoClicar={() => definirRecusando(true)}
            />
          )}
          </div>
        ) : null}
        <p className="text-xs text-textoSuave">Corrigiu a OC no Procfit? A esteira revalida sozinha a cada 10 min — ou clique em Revalidar.</p>
      </>
    );
  } else if (n.status === 'validada') {
    corpo = (
      <>
        <p className="text-[13px]">Bateu com a OC {n.pedidoCompra}. A entrada gera a NF de compra no ERP, sem estoque (o estoque entra com a cega).</p>
        <EntradaFiscal d={d} carregando={acao.isPending} resultado={acao.data} aoDarEntrada={(c) => rodar('lancar', c)} />
      </>
    );
  } else if (n.status === 'aguardando_agendamento') {
    corpo = (
      <Botao variante="primaria" className="w-full" onClick={() => aoAgendar([n.chave])}>
        <CalendarPlus className="h-4 w-4" /> Agendar recebimento
      </Botao>
    );
  } else if (n.status === 'aguardando_transferencia') {
    const rastro = transf.data?.rastro;
    // Com a emissão pelo WMS ligada, o vínculo manual é o plano B (NF emitida no Procfit).
    const pelaTela = Boolean(rastro) || Boolean(transf.data?.ligada);
    const manual = (
      <>
        {d.sugestoesTransferencia.length ? (
          <ul className="space-y-1.5">
            {d.sugestoesTransferencia.slice(0, 4).map((s) => (
              <li key={s.nfCompra} className="flex items-center justify-between gap-2 rounded-lg border border-borda px-3 py-2 text-[13px]">
                <span>
                  NF {s.numero}/{s.serie} · {s.itensEmComum}/{s.itensDaNota} itens{s.quantidadeBate ? ' · quantidades batem' : ''}
                </span>
                <Botao tamanho="micro" variante={s.quantidadeBate ? 'primaria' : 'secundaria'} carregando={acao.isPending} onClick={() => rodar('transferencia', { nfCompra: s.nfCompra })}>
                  Vincular
                </Botao>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-textoFraco">
            Nenhuma entrada no destino com todos os itens e as mesmas quantidades desta nota. Transferência parcial: digite a NF_COMPRA da entrada.
          </p>
        )}
        <div className="flex gap-2">
          <Campo inputMode="numeric" placeholder="ou digite a NF_COMPRA da entrada" value={nfDestino} onChange={(e) => definirNfDestino(e.target.value.replace(/\D/g, ''))} />
          <Botao disabled={!nfDestino} carregando={acao.isPending} onClick={() => rodar('transferencia', { nfCompra: Number(nfDestino) })}>
            Vincular
          </Botao>
        </div>
      </>
    );
    corpo = pelaTela ? (
      <>
        <p className="text-[13px]">
          {rastro
            ? `A NF de transferência ${rastro.numero ?? ''} está acompanhada acima: quando a entrada em ${n.destinoNome} aparecer, a nota anda sozinha.`
            : 'Confira a NF de transferência acima e emita. Autorizada, o WMS dá a entrada no destino (estoque transitório) e vincula sozinho.'}
        </p>
        <details className="space-y-2 text-[13px]">
          <summary className="cursor-pointer text-xs font-semibold text-textoFraco hover:text-texto">Já emitiu no Procfit? Vincular a entrada à mão</summary>
          <div className="mt-2 space-y-2">{manual}</div>
        </details>
      </>
    ) : (
      <>
        <p className="text-[13px]">
          Confira acima a NF de transferência (integral ou parcial), emita no Procfit e vincule aqui a entrada dela em <b>{n.destinoNome}</b>.
        </p>
        {manual}
      </>
    );
  } else if (n.status === 'agendada') {
    corpo = (
      <>
        <p className="text-[13px]">Quando a carga chegar em {n.destinoNome}, confirme: a cega sai na hora para imprimir em Recebimento.</p>
        <div className="flex gap-2">
          <Campo className="w-28" inputMode="numeric" placeholder="Volumes" value={volumes} onChange={(e) => definirVolumes(e.target.value.replace(/\D/g, ''))} />
          <Botao variante="primaria" className="flex-1" carregando={acao.isPending} onClick={() => rodar('chegada', { volumes: Number(volumes) || null })}>
            <PackageCheck className="h-4 w-4" /> Chegou: gerar a cega
          </Botao>
        </div>
      </>
    );
  } else if (n.status === 'em_transito_full') {
    corpo = <ChegadaNoFull d={d} carregando={acao.isPending} aoConfirmar={(c) => rodar('chegada-full', c)} />;
  } else if (n.status === 'em_conferencia' && n.recebimento && !n.recebimentoProgresso) {
    // Com progresso, o atalho (na aba do passo) mora no bloco "Recebimento da cega".
    corpo = (
      <Botao comoFilho variante="secundaria" className="w-full">
        <Link to={`/recebimento?aba=cega&recebimento=${n.recebimento}`}>
          Abrir a cega {n.recebimento} em Recebimento <ArrowRight className="h-4 w-4" />
        </Link>
      </Botao>
    );
  } else if (n.status === 'divergencia_recebimento') {
    corpo = (
      <>
        <p className="text-[13px]">
          {destinoFull
            ? `A chegada no ${n.destinoNome} foi divergente (o motivo está na linha do tempo); o que não subiu ficou no estoque transitório.`
            : `A contagem da cega ${n.recebimento ?? ''} não bate com a nota.`}{' '}
          Registre a decisão (devolução, nota complementar, ajuste).
        </p>
        <Campo placeholder="Decisão" value={motivo} onChange={(e) => definirMotivo(e.target.value)} />
        <Botao variante="primaria" className="w-full" disabled={!motivo.trim()} carregando={acao.isPending} onClick={() => rodar('divergencia-resolvida', { motivo })}>
          <CheckCircle2 className="h-4 w-4" /> Registrar decisão
        </Botao>
      </>
    );
  }

  const podeDesagendar = Boolean(d.podeDesagendar);
  if (!corpo && !podeDesagendar) return null;
  const ativo = d.podeAgir || podeDesagendar;
  return (
    <section id={`acoes-${n.chave}`} className={cn('scroll-mt-4 space-y-3 rounded-xl border p-4', ativo ? 'border-primaria/30 bg-primaria-suave/30' : 'border-borda')}>
      <p className="rotulo">{ativo ? 'O que fazer agora' : 'O que falta'}</p>
      {corpo}
      {podeDesagendar ? (
        <Desagendar
          carregando={acao.isPending}
          aoReagendar={() => aoAgendar([n.chave])}
          aoDesagendar={(m) => acao.mutate({ caminho: 'desagendar', corpo: { motivo: m } })}
        />
      ) : null}
      {erro ? <p className="rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">{erro}</p> : null}
    </section>
  );
}

/**
 * Deu problema no agendamento: reagendar (outra data ou destino, a gaveta de
 * sempre) ou desagendar (volta para "A agendar", com o motivo na linha do
 * tempo). Com NF de transferência já emitida, a API recusa e diz o caminho.
 */
function Desagendar({
  carregando,
  aoReagendar,
  aoDesagendar,
}: {
  carregando: boolean;
  aoReagendar: () => void;
  aoDesagendar: (motivo: string) => void;
}): JSX.Element {
  const [aberto, definirAberto] = React.useState(false);
  const [motivo, definirMotivo] = React.useState('');
  return aberto ? (
    <div className="space-y-2.5 rounded-lg border border-status-divergencia/30 bg-superficie p-3">
      <p className="flex items-center gap-2 text-[13px] font-semibold text-status-divergencia">
        <CalendarX2 className="h-4 w-4" /> Desagendar
      </p>
      <p className="text-xs text-textoFraco">A nota volta para “A agendar”, sem destino nem data. O motivo fica na linha do tempo.</p>
      <label className="block space-y-1">
        <span className="text-xs font-semibold text-texto">Motivo</span>
        <Campo placeholder="Ex.: fornecedor não coletou, carga remarcada" value={motivo} onChange={(e) => definirMotivo(e.target.value)} />
      </label>
      <div className="flex justify-end gap-2">
        <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirAberto(false)}>
          Voltar
        </Botao>
        <Botao variante="perigo" tamanho="pequeno" disabled={!motivo.trim()} carregando={carregando} onClick={() => aoDesagendar(motivo)}>
          <CalendarX2 className="h-4 w-4" /> Confirmar desagendamento
        </Botao>
      </div>
    </div>
  ) : (
    // Duas saídas lado a lado, cada uma dizendo o que faz: antes eram dois links de texto soltos.
    <div className="grid gap-2 sm:grid-cols-2">
      <button
        type="button"
        onClick={aoReagendar}
        className="flex items-center gap-2.5 rounded-lg border border-borda bg-superficie px-3 py-2.5 text-left transition hover:border-primaria/50 hover:bg-primaria-suave/40"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-primaria-suave text-primaria">
          <CalendarClock className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <b className="block text-[13px] text-texto">Reagendar</b>
          <span className="block text-xs text-textoFraco">Outra data, janela ou destino</span>
        </span>
      </button>
      <button
        type="button"
        onClick={() => definirAberto(true)}
        className="flex items-center gap-2.5 rounded-lg border border-borda bg-superficie px-3 py-2.5 text-left transition hover:border-status-divergencia/50 hover:bg-status-divergenciaFundo/60"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-status-divergenciaFundo text-status-divergencia">
          <CalendarX2 className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <b className="block text-[13px] text-texto">Desagendar</b>
          <span className="block text-xs text-textoFraco">Volta para “A agendar”, com motivo</span>
        </span>
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ Slack

type Marcar = 'ninguem' | 'pessoas' | 'todos';

interface PessoaSlack {
  id: string;
  nome: string;
  titulo: string | null;
  foto: string | null;
  /** Ligou o Slack ao WMS: a resposta dela na thread age na nota. */
  noWms: boolean;
}

/** Foto do Slack; sem foto, as iniciais. */
function Avatar({ pessoa, tamanho }: { pessoa: PessoaSlack; tamanho: string }): JSX.Element {
  const iniciais = pessoa.nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  return pessoa.foto ? (
    <img src={pessoa.foto} alt="" className={cn(tamanho, 'shrink-0 rounded-md object-cover')} />
  ) : (
    <span className={cn(tamanho, 'flex shrink-0 items-center justify-center rounded-md bg-[#611f69] text-[10px] font-bold text-white')}>{iniciais}</span>
  );
}

/**
 * Conversa da nota no Slack. Quem escreve escolhe quem marcar: ninguém (o
 * padrão), pessoas do canal ou @channel. A mensagem leva sempre a nota numa
 * linha (fornecedor, NF, valor, OC), para quem lê a notificação solta.
 */
function Conversa({
  d,
  textoInicial,
  aoEnviar,
}: {
  d: DetalheNota;
  /** Texto pronto (ex.: o aviso de divergência): já abre em "Pessoas do canal". */
  textoInicial?: string;
  aoEnviar?: () => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const [texto, definirTexto] = React.useState(textoInicial ?? '');
  const [marcar, definirMarcar] = React.useState<Marcar>(textoInicial ? 'pessoas' : 'ninguem');
  // DANFE/XML do acervo vão como arquivo na mesma thread (quem é marcado nem sempre tem WMS).
  const [anexar, definirAnexar] = React.useState<('pdf' | 'xml')[]>(d.nota.resumo ? [] : ['pdf']);
  const [avisoAnexo, definirAvisoAnexo] = React.useState<string | null>(null);
  const [escolhidos, definirEscolhidos] = React.useState<string[]>([]);
  const [busca, definirBusca] = React.useState('');
  const campoTexto = React.useRef<HTMLTextAreaElement>(null);
  const secao = React.useRef<HTMLElement>(null);
  // Texto pronto de várias linhas: o campo já abre do tamanho dele.
  React.useEffect(() => {
    const c = campoTexto.current;
    if (!textoInicial || !c) return;
    c.style.height = 'auto';
    c.style.height = `${Math.min(c.scrollHeight, 320)}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Outro quadro da gaveta (ex.: "Entrou com divergência") pode mandar um texto
  // pronto: entra aqui, a pessoa escolhe quem marcar e envia como ela mesma.
  React.useEffect(() => {
    const ouvir = (e: Event): void => {
      const pronto = (e as CustomEvent<{ chave: string; texto: string; marcar?: string[] }>).detail;
      if (pronto.chave !== d.nota.chave) return;
      definirTexto(pronto.texto);
      definirMarcar('pessoas');
      if (pronto.marcar?.length) definirEscolhidos(pronto.marcar);
      secao.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => campoTexto.current?.focus(), 350);
    };
    window.addEventListener('wms-conversa-preencher', ouvir);
    return () => window.removeEventListener('wms-conversa-preencher', ouvir);
  }, [d.nota.chave]);
  const membros = useQuery<{ canal: string | null; pessoas: PessoaSlack[] }>({
    queryKey: ['entrada-slack-membros', d.nota.chave],
    queryFn: () => api.get(`/api/entradas/nota/${d.nota.chave}/slack-membros`),
    enabled: marcar === 'pessoas',
    // Quem entra no canal aparece na próxima abertura do seletor.
    staleTime: 30_000,
  });
  const enviar = useMutation({
    mutationFn: () =>
      api.post<{ link: string | null; anexos?: { tipo: string; ok: boolean; erro?: string }[] }>(`/api/entradas/nota/${d.nota.chave}/slack`, {
        texto,
        marcar: marcar === 'pessoas' ? escolhidos : marcar,
        anexar,
      }),
    onSuccess: (r) => {
      const falhou = (r.anexos ?? []).filter((a) => !a.ok);
      definirAvisoAnexo(falhou.length ? `Mensagem enviada, mas ${falhou.map((a) => (a.tipo === 'pdf' ? 'a DANFE' : 'o XML')).join(' e ')} não foi anexado: ${falhou[0]!.erro}` : null);
      if (falhou.length) {
        // Deixa o aviso à vista em vez de fechar o editor.
        void cliente.invalidateQueries({ queryKey: ['entrada', d.nota.chave] });
        definirTexto('');
        return;
      }
      definirTexto('');
      if (campoTexto.current) campoTexto.current.style.height = 'auto';
      definirEscolhidos([]);
      definirMarcar('ninguem');
      definirAnexar(d.nota.resumo ? [] : ['pdf']);
      void cliente.invalidateQueries({ queryKey: ['entrada', d.nota.chave] });
      if (r.link && !d.slackLink) window.open(r.link, '_blank', 'noopener');
      aoEnviar?.();
    },
  });
  const alternar = (id: string): void => definirEscolhidos((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));
  const termo = busca.trim().toLowerCase();
  const pessoas = (membros.data?.pessoas ?? []).filter(
    (p) => !termo || p.nome.toLowerCase().includes(termo) || (p.titulo ?? '').toLowerCase().includes(termo),
  );
  const marcadas = (membros.data?.pessoas ?? []).filter((p) => escolhidos.includes(p.id));
  const opcao = (v: Marcar, rotulo: string) => (
    <button
      type="button"
      onClick={() => definirMarcar(v)}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs font-semibold transition',
        marcar === v ? 'border-[#611f69] bg-[#611f69] text-white' : 'border-borda bg-superficie text-textoFraco hover:border-bordaForte',
      )}
    >
      {rotulo}
    </button>
  );
  const semPessoa = marcar === 'pessoas' && !escolhidos.length;

  return (
    <section ref={secao} className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="rotulo flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5" /> Conversa no Slack
        </p>
        {d.slackLink ? (
          <a href={d.slackLink} target="_blank" rel="noopener noreferrer" className={cn(acaoTexto, 'text-xs')}>
            Abrir a thread <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-textoSuave">Marcar:</span>
        {opcao('ninguem', 'Ninguém')}
        {opcao('pessoas', escolhidos.length ? `Pessoas (${escolhidos.length})` : 'Pessoas do canal')}
        {opcao('todos', '@channel (todos)')}
      </div>
      {marcar === 'pessoas' ? (
        <div className="overflow-hidden rounded-xl border border-borda bg-superficie">
          {/* Quem vai ser marcado, como no "Para:" do Slack. */}
          <div className="flex min-h-10 flex-wrap items-center gap-1.5 border-b border-borda px-3 py-2">
            <span className="text-xs font-semibold text-textoSuave">Para:</span>
            {marcadas.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-[#e8f5fa] py-0.5 pl-0.5 pr-1.5 text-xs font-semibold text-[#1264a3]">
                <Avatar pessoa={p} tamanho="h-5 w-5" />@{p.nome}
                <button type="button" aria-label={`Tirar ${p.nome}`} className="ml-0.5 rounded-full px-1 hover:bg-[#d1ebf5]" onClick={() => alternar(p.id)}>
                  ×
                </button>
              </span>
            ))}
            <input
              autoFocus
              className="min-w-[8rem] flex-1 border-0 bg-transparent p-0 text-sm shadow-none outline-none ring-0 placeholder:text-textoSuave focus:border-0 focus:outline-none focus:ring-0"
              placeholder={marcadas.length ? 'Marcar mais alguém…' : 'Buscar por nome ou cargo…'}
              value={busca}
              onChange={(e) => definirBusca(e.target.value)}
            />
          </div>
          {membros.isLoading ? <p className="px-3 py-3 text-xs text-textoSuave">Lendo quem está no canal…</p> : null}
          {membros.error ? (
            <p className="px-3 py-3 text-xs text-status-divergencia">{membros.error instanceof Error ? membros.error.message : String(membros.error)}</p>
          ) : null}
          <ul className="max-h-60 divide-y divide-borda overflow-y-auto">
            {pessoas.map((p) => {
              const marcada = escolhidos.includes(p.id);
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => alternar(p.id)}
                    className={cn('flex w-full items-center gap-3 px-3 py-2 text-left transition', marcada ? 'bg-[#f2f9fc]' : 'hover:bg-superficie2')}
                  >
                    <Avatar pessoa={p} tamanho="h-8 w-8" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold">{p.nome}</span>
                      <span className="block truncate text-xs text-textoSuave">{p.titulo || 'sem cargo no Slack'}</span>
                    </span>
                    {p.noWms ? (
                      <span className="shrink-0 rounded-full bg-status-livreFundo px-2 py-0.5 text-2xs font-semibold text-status-livre" title="Ligou o Slack ao WMS: a resposta dele age na nota">
                        no WMS
                      </span>
                    ) : null}
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[11px] font-bold',
                        marcada ? 'border-[#1264a3] bg-[#1264a3] text-white' : 'border-bordaForte bg-superficie',
                      )}
                    >
                      {marcada ? '✓' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
            {!membros.isLoading && membros.data && !pessoas.length ? (
              <li className="px-3 py-3 text-xs text-textoSuave">{termo ? 'Ninguém do canal com esse nome ou cargo.' : 'Ninguém no canal além do bot.'}</li>
            ) : null}
          </ul>
          {membros.data ? (
            <div className="flex items-center justify-between border-t border-borda bg-superficie2 px-3 py-1.5 text-2xs text-textoSuave">
              <span>
                {membros.data.pessoas.length} no canal · {escolhidos.length ? `${escolhidos.length} marcada(s)` : 'ninguém marcado'}
              </span>
              {escolhidos.length ? (
                <button type="button" className={acaoTexto} onClick={() => definirEscolhidos([])}>
                  Limpar
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {marcar === 'todos' ? <p className="text-2xs text-textoSuave">Avisa todo mundo do canal. Use só quando for urgente.</p> : null}
      {d.nota.resumo ? null : (
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-textoSuave">Anexar na thread:</span>
          {(
            [
              ['pdf', 'DANFE (PDF)'],
              ['xml', 'XML'],
            ] as const
          ).map(([t, rotulo]) => (
            <label key={t} className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-[rgb(var(--cor-primaria))]"
                checked={anexar.includes(t)}
                onChange={(e) => definirAnexar((a) => (e.target.checked ? [...a, t] : a.filter((x) => x !== t)))}
              />
              {rotulo}
            </label>
          ))}
        </div>
      )}
      {avisoAnexo ? <p className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-xs text-status-atencao">{avisoAnexo}</p> : null}
      <div className="flex items-end gap-2">
        {/* Enter envia, Shift+Enter quebra a linha (como no Slack). Cresce até 5 linhas. */}
        <textarea
          ref={campoTexto}
          rows={1}
          className={cn(
            'max-h-32 min-h-toque w-full resize-none rounded-lg border border-borda bg-superficie px-3.5 py-2.5 text-sm text-texto shadow-xs',
            'transition-colors placeholder:text-textoSuave hover:border-bordaForte',
            'focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12',
          )}
          placeholder={d.nota.slackThread ? 'Escreva na thread da nota' : 'Escreva a mensagem (abre uma thread da nota no canal)'}
          value={texto}
          onChange={(e) => {
            definirTexto(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
            e.preventDefault();
            if (texto.trim() && !semPessoa && !enviar.isPending) enviar.mutate();
          }}
        />
        <Botao
          aria-label="Enviar no Slack"
          carregando={enviar.isPending}
          disabled={(!texto.trim() && Boolean(d.nota.slackThread)) || semPessoa}
          onClick={() => enviar.mutate()}
        >
          <Send className="h-4 w-4" />
        </Botao>
      </div>
      {enviar.error ? <p className="text-xs text-status-divergencia">{enviar.error instanceof Error ? enviar.error.message : String(enviar.error)}</p> : null}
      <p className="text-2xs text-textoSuave">
        <b>Enter</b> envia · <b>Shift+Enter</b> quebra a linha. As respostas na thread voltam para cá; o nº da OC, `revalidar` e `recusar motivo` agem na nota.
      </p>
    </section>
  );
}

// ------------------------------------------------------------------ itens e histórico

/** O que a tela de Recebimento devolve por cega (`/api/recebimentos/:r/etiquetas-alocacao`). */
interface EtiquetasDaCega {
  aprovado: boolean;
  aGerar: { produto: number; descricao: string | null; conferida: number }[];
  etiquetas: { produto: number; descricao: string | null; quantidade: number; sequencia: number; codigoBarras: string; alocada: boolean }[];
}

/**
 * Em conferência: o que já foi guardado e o que falta, por produto — a pergunta
 * do CD depois da cega (Paulo, 29/10/2026). Os mesmos dados da tela de
 * Recebimento, e o atalho para ela na aba de cada passo.
 */
function FaltaAlocar({ d }: { d: DetalheNota }): JSX.Element | null {
  const p = d.nota.recebimentoProgresso;
  const recebimento = p?.recebimento ?? null;
  const aberto = Boolean(p && p.aprovado && (p.fase === 'parcial' || p.fase === 'alocar' || p.fase === 'etiqueta'));
  const consulta = useQuery<EtiquetasDaCega>({
    queryKey: ['entradas-falta-alocar', recebimento],
    queryFn: () => api.get<EtiquetasDaCega>(`/api/recebimentos/${recebimento}/etiquetas-alocacao`),
    enabled: aberto,
    staleTime: 30_000,
  });
  if (!p) return null;

  const pct = p.etiquetas ? Math.round((p.alocadas / p.etiquetas) * 100) : 0;
  const aba = p.fase === 'contagem' ? 'cega' : p.fase === 'aprovacao' ? 'aprovar' : p.fase === 'etiqueta' ? 'etiquetas' : 'alocar';
  // Etiquetas não bipadas, agrupadas por produto: é o que se procura no chão.
  const pendentes = new Map<number, { descricao: string | null; volumes: number; unidades: number; sequencias: number[] }>();
  for (const e of consulta.data?.etiquetas ?? []) {
    if (e.alocada) continue;
    const g = pendentes.get(e.produto) ?? { descricao: e.descricao, volumes: 0, unidades: 0, sequencias: [] };
    g.volumes++;
    g.unidades += e.quantidade;
    g.sequencias.push(e.sequencia);
    pendentes.set(e.produto, g);
  }

  return (
    <section className="space-y-2 rounded-xl border border-borda p-4">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[13px] font-semibold">Recebimento da cega {p.recebimento}</p>
        <Pilula tom={p.fase === 'parcial' ? 'alerta' : p.fase === 'alocada' ? 'ok' : 'marca'}>
          {SUBETAPAS_RECEBIMENTO.find((s) => s.fase === p.fase)?.rotulo}
        </Pilula>
      </div>
      <ol className="flex gap-1 text-2xs font-semibold">
        {[
          ['Contada', p.conferido],
          ['Aprovada', p.aprovado],
          ['Etiquetada', p.aprovado && p.etiquetas > 0 && p.produtosSemEtiqueta === 0],
          ['Alocada', p.etiquetas > 0 && p.alocadas === p.etiquetas && p.produtosSemEtiqueta === 0],
        ].map(([rotulo, feito]) => (
          <li
            key={String(rotulo)}
            className={cn('flex-1 rounded-md px-2 py-1 text-center', feito ? 'bg-status-livreFundo text-status-livre' : 'bg-superficie2 text-textoSuave')}
          >
            {feito ? '✓ ' : ''}
            {rotulo}
          </li>
        ))}
      </ol>
      {p.etiquetas > 0 ? (
        <div>
          <div className="flex justify-between text-xs text-textoFraco">
            <span>
              <b className="tabular text-texto">{p.alocadas}</b> de <b className="tabular text-texto">{p.etiquetas}</b> volume(s) alocado(s)
            </span>
            <span className="tabular">
              {p.unidadesAlocadas.toLocaleString('pt-BR')} de {p.unidades.toLocaleString('pt-BR')} un.
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-superficie2">
            <div className={cn('h-full rounded-full', pct === 100 ? 'bg-status-livre' : 'bg-status-atencao')} style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : null}
      {aberto ? (
        consulta.isLoading ? (
          <Esqueleto className="h-16" />
        ) : consulta.isError ? (
          <p className="text-xs text-textoSuave">Sem acesso ao detalhe da cega (precisa ver o estoque do centro).</p>
        ) : (
          <div className="max-h-60 overflow-auto rounded-lg border border-borda">
            <table className="w-full text-xs">
              <thead className="bg-superficie2 text-left text-textoFraco">
                <tr>
                  <th className="px-3 py-1.5">Falta alocar</th>
                  <th className="px-3 py-1.5 text-right">Volumes</th>
                  <th className="px-3 py-1.5 text-right">Un.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {(consulta.data?.aGerar ?? []).map((a) => (
                  <tr key={`g${a.produto}`} className="bg-status-atencaoFundo/40">
                    <td className="px-3 py-1.5">
                      <span className="block max-w-[220px] truncate">{a.descricao ?? a.produto}</span>
                      <span className="text-textoSuave">{a.produto} · sem etiqueta</span>
                    </td>
                    <td className="px-3 py-1.5 text-right text-textoSuave">—</td>
                    <td className="px-3 py-1.5 text-right tabular">{a.conferida.toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
                {[...pendentes.entries()].map(([produto, g]) => (
                  <tr key={produto}>
                    <td className="px-3 py-1.5">
                      <span className="block max-w-[220px] truncate">{g.descricao ?? produto}</span>
                      <span className="text-textoSuave">
                        {produto} · etiqueta(s) {g.sequencias.length > 6 ? `${g.sequencias.slice(0, 6).join(', ')}…` : g.sequencias.join(', ')}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular">{g.volumes}</td>
                    <td className="px-3 py-1.5 text-right tabular">{g.unidades.toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
                {!consulta.data?.aGerar.length && !pendentes.size ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-textoSuave">Nada pendente de alocação.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )
      ) : null}
      <Botao comoFilho variante="secundaria" tamanho="pequeno" className="w-full">
        <Link to={`/recebimento?aba=${aba}&recebimento=${p.recebimento}`}>
          Abrir a cega {p.recebimento} em Recebimento <ArrowRight className="h-4 w-4" />
        </Link>
      </Botao>
    </section>
  );
}

function Itens({ d }: { d: DetalheNota }): JSX.Element {
  return (
    <details className="rounded-xl border border-borda">
      <summary className="cursor-pointer select-none px-4 py-2.5 text-[13px] font-semibold">
        Itens da nota ({d.itens.length})
      </summary>
      <div className="max-h-72 overflow-auto border-t border-borda">
        <table className="w-full text-xs">
          <thead className="bg-superficie2 text-left text-textoFraco">
            <tr>
              <th className="px-3 py-1.5">#</th>
              <th className="px-3 py-1.5">Produto</th>
              <th className="px-3 py-1.5 text-right">Qtd</th>
              <th className="px-3 py-1.5 text-right">Valor</th>
              <th className="px-3 py-1.5">xPed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {d.itens.map((i) => (
              <tr key={i.nItem} className={cn(i.produto == null && 'bg-status-divergenciaFundo/50')}>
                <td className="px-3 py-1.5 tabular">{i.nItem}</td>
                <td className="px-3 py-1.5">
                  <span className="block max-w-[200px] truncate">{i.descricao}</span>
                  <span className="text-textoSuave">{i.produto ?? 'sem cadastro'} · {i.cprod}</span>
                </td>
                <td className="px-3 py-1.5 text-right tabular">
                  {i.quantidade?.toLocaleString('pt-BR')} {i.unidade}
                </td>
                <td className="px-3 py-1.5 text-right tabular">{moeda(i.valorTotal)}</td>
                <td className="px-3 py-1.5">{i.xPed ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * O que aconteceu com a nota, do mais novo para o mais velho — status, ações
 * e a conversa do Slack. "Atualizar" lê de novo (e busca respostas novas na
 * thread) sem fechar a gaveta; ela também se atualiza sozinha a cada 30 s.
 */
function LinhaDoTempo({
  chave,
  eventos,
  atualizando,
  aoAtualizar,
}: {
  chave: string;
  eventos: Evento[];
  atualizando: boolean;
  aoAtualizar: () => void;
}): JSX.Element {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <p className="rotulo">Linha do tempo</p>
        <button
          type="button"
          onClick={aoAtualizar}
          disabled={atualizando}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-primaria hover:bg-primaria-suave/40 disabled:opacity-60"
          title="Busca interações novas (inclusive respostas no Slack)"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', atualizando && 'animate-spin')} /> {atualizando ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>
      <div className="mb-2 flex flex-wrap gap-3 text-2xs text-textoSuave">
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-[#e0e7ff] ring-1 ring-[#a5b4fc]" /> saiu do WMS
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-[#dcfce7] ring-1 ring-[#86efac]" /> resposta no Slack
        </span>
      </div>
      <ol className="space-y-3 border-l border-borda pl-4">
        {[...eventos].reverse().map((e, i) => (
          e.tipo === 'slack' ? (
            <li key={i} className={cn('relative flex text-[13px]', e.lado === 'enviada' ? 'justify-end' : 'justify-start')}>
              <span className="absolute -left-[21px] top-2 h-2.5 w-2.5 rounded-full bg-[#611f69] ring-2 ring-superficie" />
              <div
                className={cn(
                  'max-w-[88%] rounded-2xl px-3 py-2 ring-1',
                  e.lado === 'enviada' ? 'rounded-br-sm bg-[#eef2ff] ring-[#c7d2fe]' : 'rounded-bl-sm bg-[#f0fdf4] ring-[#bbf7d0]',
                  e.marca === 'apagada' && 'opacity-60',
                )}
              >
                <p className={cn('text-2xs font-semibold', e.lado === 'enviada' ? 'text-[#4338ca]' : 'text-[#15803d]')}>
                  {e.autor.replace(/ → Slack$/, '').replace(/^Slack: /, '')}
                  <span className="font-normal text-textoSuave">
                    {' · '}
                    {new Date(e.quando).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    {e.marca === 'editada' ? ' · editada' : ''}
                  </span>
                </p>
                <p className={cn('whitespace-pre-line leading-snug', e.marca === 'apagada' && 'line-through')}>{e.texto}</p>
                {e.marca === 'apagada' ? <p className="mt-0.5 text-2xs font-semibold text-textoSuave">apagada no Slack</p> : null}
              </div>
            </li>
          ) : (
          <li key={i} className="relative text-[13px]">
            <span
              className={cn(
                'absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-superficie',
                e.tipo === 'status' ? 'bg-primaria' : 'bg-bordaForte',
              )}
            />
            <p className="text-2xs text-textoSuave">
              {new Date(e.quando).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · {e.autor}
            </p>
            <p className="leading-snug">{e.texto ?? (e.para ? `→ ${e.para}` : '')}</p>
            {/* Anexo que foi para o Slack: abre o mesmo arquivo do acervo. */}
            {e.texto?.startsWith('📎 ') ? (
              <a
                href={`/api/entradas/nota/${chave}/arquivo/${e.texto.startsWith('📎 XML') ? 'xml' : 'pdf'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 rounded-md border border-borda px-2 py-0.5 text-2xs font-semibold text-primaria hover:border-primaria"
              >
                <ExternalLink className="h-3 w-3" /> Ver {e.texto.startsWith('📎 XML') ? 'XML' : 'DANFE'}
              </a>
            ) : null}
          </li>
          )
        ))}
      </ol>
    </section>
  );
}

/**
 * Divergente na validação: a comparação é com o pré-pedido aprovado, então
 * editar a OC não fecha mais a divergência. Quem decide aceita aqui, com o
 * motivo; o aceite fica gravado e a nota reconfere (sem mais nada, Validada).
 */
function AceitarNaValidacao({ chave }: { chave: string }): JSX.Element | null {
  const { temPermissao } = useSessao();
  const acao = useAcao(chave);
  const [aberto, definirAberto] = React.useState(false);
  const [motivo, definirMotivo] = React.useState('');
  if (!(temPermissao('entradas.comprador') || temPermissao('entradas.fiscal'))) return null;
  if (!aberto) {
    return (
      <CartaoAcao
        icone={CheckCircle2}
        tom="ok"
        titulo="Aceitar a divergência"
        ajuda="Preço ou quantidade combinados com o fornecedor: a nota segue, com o motivo registrado"
        aoClicar={() => definirAberto(true)}
      />
    );
  }
  return (
    <div className="sm:col-span-2">
      <FormAceite
        motivo={motivo}
        definirMotivo={definirMotivo}
        carregando={acao.isPending}
        aoVoltar={() => definirAberto(false)}
        aoAceitar={() => acao.mutate({ caminho: 'aceitar-divergencia', corpo: { motivo } }, { onSuccess: () => definirAberto(false) })}
      />
    </div>
  );
}

/** O formulário do aceite: título, motivo com rótulo e os dois botões. */
function FormAceite({
  motivo,
  definirMotivo,
  carregando,
  aoVoltar,
  aoAceitar,
}: {
  motivo: string;
  definirMotivo: (v: string) => void;
  carregando: boolean;
  aoVoltar: () => void;
  aoAceitar: () => void;
}): JSX.Element {
  return (
    <div className="space-y-2.5 rounded-lg border border-status-livre/40 bg-superficie p-3">
      <p className="flex items-center gap-2 text-[13px] font-semibold text-status-livre">
        <CheckCircle2 className="h-4 w-4" /> Aceitar a divergência
      </p>
      <p className="text-xs text-textoFraco">Fecha as divergências abertas desta nota. O motivo vai para a linha do tempo e para a thread do Slack.</p>
      <label className="block space-y-1">
        <span className="text-xs font-semibold text-texto">Motivo</span>
        <Campo placeholder="Ex.: reajuste de preço combinado com o fornecedor" value={motivo} onChange={(e) => definirMotivo(e.target.value)} />
      </label>
      <div className="flex justify-end gap-2">
        <Botao variante="fantasma" tamanho="pequeno" onClick={aoVoltar}>
          Voltar
        </Botao>
        <Botao variante="primaria" tamanho="pequeno" disabled={!motivo.trim()} carregando={carregando} onClick={aoAceitar}>
          <CheckCircle2 className="h-4 w-4" /> Confirmar aceite
        </Botao>
      </div>
    </div>
  );
}

const TOM_CARTAO = {
  ok: { borda: 'hover:border-status-livre/50 hover:bg-status-livreFundo/50', icone: 'bg-status-livreFundo text-status-livre' },
  marca: { borda: 'hover:border-primaria/50 hover:bg-primaria-suave/40', icone: 'bg-primaria-suave text-primaria' },
  perigo: { borda: 'hover:border-status-divergencia/50 hover:bg-status-divergenciaFundo/60', icone: 'bg-status-divergenciaFundo text-status-divergencia' },
} as const;

/** Uma ação como cartão (ícone, o que é, o que faz): link de texto solto não parece botão. */
function CartaoAcao({
  icone: Icone,
  tom,
  titulo,
  ajuda,
  aoClicar,
  ativo,
}: {
  icone: typeof CheckCircle2;
  tom: keyof typeof TOM_CARTAO;
  titulo: string;
  ajuda: string;
  aoClicar: () => void;
  ativo?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={aoClicar}
      aria-pressed={ativo}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg border bg-superficie px-3 py-2.5 text-left transition',
        ativo ? 'border-primaria ring-2 ring-primaria/20' : 'border-borda',
        TOM_CARTAO[tom].borda,
      )}
    >
      <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-md', TOM_CARTAO[tom].icone)}>
        <Icone className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <b className="block text-[13px] text-texto">{titulo}</b>
        <span className="block text-xs text-textoFraco">{ajuda}</span>
      </span>
    </button>
  );
}

/**
 * A entrada fiscal foi feita no ERP com a nota ainda divergente da OC: a nota
 * seguiu, mas a divergência fica à vista até alguém decidir. "Aceitar" fecha
 * com motivo (vai para a linha do tempo e para a thread do Slack).
 */
function EntrouComDivergencia({ d }: { d: DetalheNota }): JSX.Element | null {
  const n = d.nota;
  const { temPermissao } = useSessao();
  const acao = useAcao(n.chave);
  const [motivo, definirMotivo] = React.useState('');
  const [aceitando, definirAceitando] = React.useState(false);
  const [avisando, definirAvisando] = React.useState(false);
  if (!(n.divergenciasAbertas > 0) || ['divergente', 'sem_oc', 'recusada'].includes(n.status)) return null;
  const pode = temPermissao('entradas.comprador') || temPermissao('entradas.fiscal');
  const erro = acao.error instanceof ErroHttp ? acao.error.message : acao.error ? String(acao.error) : null;
  return (
    <section className="space-y-2.5 rounded-xl border border-status-divergencia/40 bg-status-divergenciaFundo/40 p-4">
      <p className="flex items-center gap-2 text-[13px] font-bold text-status-divergencia">
        <XCircle className="h-4 w-4" /> Entrou com divergência da OC
      </p>
      <p className="text-xs text-textoFraco">
        A entrada fiscal foi feita no ERP do jeito que a nota veio, e a nota seguiu. Falta decidir: aceitar, pedir correção ou devolver.
      </p>
      <TabelaDivergencias d={d} />
      {!pode ? null : aceitando ? (
        <FormAceite
          motivo={motivo}
          definirMotivo={definirMotivo}
          carregando={acao.isPending}
          aoVoltar={() => definirAceitando(false)}
          aoAceitar={() => acao.mutate({ caminho: 'aceitar-divergencia', corpo: { motivo } }, { onSuccess: () => definirAceitando(false) })}
        />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          <CartaoAcao
            icone={CheckCircle2}
            tom="ok"
            titulo="Aceitar a divergência"
            ajuda="Combinado com o fornecedor: fecha com o motivo registrado"
            aoClicar={() => definirAceitando(true)}
          />
          {/* Abre o editor da Conversa aqui mesmo, com o texto pronto: quem envia é a pessoa, e ela escolhe quem marcar. */}
          <CartaoAcao
            icone={Send}
            tom="marca"
            titulo={avisando ? 'Fechar o aviso' : 'Avisar no Slack'}
            ajuda="Pedir correção ou devolução, com as divergências já escritas"
            ativo={avisando}
            aoClicar={() => definirAvisando((x) => !x)}
          />
        </div>
      )}
      {avisando ? (
        <div className="rounded-lg border border-borda bg-superficie p-3">
          <Conversa d={d} textoInicial={textoDaDivergencia(d)} aoEnviar={() => definirAvisando(false)} />
        </div>
      ) : null}
      {erro ? <p className="text-xs text-status-divergencia">{erro}</p> : null}
    </section>
  );
}

/** "Nota 50 un · saldo do pedido 25 un" (novo) ou "a nota traz 50 e a OC N tem saldo de 25" (antigo). */
function qtdDaMensagem(msg: string): [string, string] | null {
  const m = /Nota ([\d.,]+) un · saldo do pedido (-?[\d.,]+) un/.exec(msg) ?? /a nota traz ([\d.,]+) e a OC \d+ tem saldo de (-?[\d.,]+)/.exec(msg);
  return m ? [m[1]!, m[2]!] : null;
}

/**
 * O pedido de decisão, curto: o que diverge (item, nota × OC) e a pergunta.
 * A linha da nota (NF, fornecedor, valor, chave) a Conversa já põe embaixo.
 */
function textoDaDivergencia(d: DetalheNota): string {
  const real = (v: string): number => Number(v.replace(/\./g, '').replace(',', '.'));
  const brl = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  // Valor da linha na nota (o total do item, sem depender da unidade de compra × estoque).
  const valorDoItem = (nItem: number | null): number | null => d.itens.find((i) => i.nItem === nItem)?.valorTotal ?? null;
  let total = 0;
  // Um bloco por item: o que é, quanto veio na nota, quanto a OC diz, a diferença e quanto ela soma em R$.
  const blocos = d.divergencias.slice(0, 4).map((x) => {
    const sku = /^Item \d+ · SKU (\d+)/.exec(x.mensagem)?.[1] ?? null;
    const desc = /^Item \d+ (?:· SKU \d+ )?\((?:[^-]*-\s*)?([^)]*)\)/.exec(x.mensagem)?.[1]?.trim().slice(0, 50) ?? null;
    const item = desc ? `${sku ? `SKU ${sku} · ` : ''}${desc}` : null;
    const preco = precosDaMensagem(x.mensagem);
    if (item && preco) {
      const nota = preco.nota;
      const oc = preco.ref;
      const dif = nota - oc;
      const linha = valorDoItem(x.nItem);
      const aMais = linha != null && nota > 0 ? (linha * dif) / nota : null;
      if (aMais != null) total += aMais;
      return (
        `*${item}*\n` +
        `• Nota: ${brl(nota)} por unidade\n` +
        (preco.pre != null
          ? `• Pré-pedido ${preco.prePedido} (aprovado): ${brl(preco.pre)}\n` +
            (preco.oc != null && Math.abs(preco.oc - preco.pre) > 0.005 ? `• OC hoje: ${brl(preco.oc)} (alterada depois do pré-pedido)\n` : '')
          : `• OC: ${brl(oc)}\n`) +
        `• Diferença: ${dif >= 0 ? '+' : '−'}${brl(Math.abs(dif))} (${dif >= 0 ? '+' : '−'}${Math.abs((dif / oc) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)` +
        (aMais != null ? `\n• No item: ${aMais >= 0 ? '+' : '−'}${brl(Math.abs(aMais))}` : '')
      );
    }
    const qtd = qtdDaMensagem(x.mensagem);
    if (item && qtd) {
      const traz = real(qtd[0]);
      const excede = traz - Math.max(0, real(qtd[1]));
      const linha = valorDoItem(x.nItem);
      const aMais = linha != null && traz > 0 && excede > 0 ? (linha * excede) / traz : null;
      if (aMais != null) total += aMais;
      return `*${item}*\n• Nota: ${qtd[0]} un.\n• Saldo do pedido: ${qtd[1]} un.` + (aMais != null ? `\n• Acima do pedido: +${brl(aMais)}` : '');
    }
    return `• ${x.mensagem.replace(/\s*\(toler[^)]*\)/, '').replace(/\s*São R\$.*$/, '')}`;
  });
  const linhaTotal = total ? `\n\n*Total da divergência: ${total >= 0 ? '+' : '−'}${brl(Math.abs(total))}*` : '';
  return `*Divergência com a OC${d.nota.pedidoCompra ? ` ${d.nota.pedidoCompra}` : ''}*\n\n${blocos.join('\n\n')}${linhaTotal}\n\nAceitamos, pedimos correção ou devolvemos?`;
}

/**
 * As divergências em tabela: tipo, item (SKU e descrição), o que veio na nota
 * (quantidade e valor) e o que a OC diz, com a diferença. Divergência da nota
 * inteira (fornecedor, empresa, OC encerrada, sem OC) vai numa linha só.
 */
function TabelaDivergencias({ d }: { d: DetalheNota }): JSX.Element | null {
  if (!d.divergencias.length) return null;
  const brl = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const real = (v: string | null | undefined): number | null => {
    const m = /(-?[\d.]+,\d+|-?[\d.]+)/.exec(v ?? '');
    return m ? Number(m[1]!.replace(/\./g, '').replace(',', '.')) : null;
  };
  return (
    <div className="overflow-x-auto rounded-lg border border-borda bg-superficie">
      <table className="w-full text-xs">
        <thead className="bg-superficie2 text-left text-2xs uppercase tracking-wide text-textoSuave">
          <tr>
            <th className="px-2 py-1.5">Tipo</th>
            <th className="px-2 py-1.5">Item</th>
            <th className="px-2 py-1.5 text-right">Nota</th>
            <th className="px-2 py-1.5 text-right">Pedido</th>
            <th className="px-2 py-1.5 text-right">Diferença</th>
          </tr>
        </thead>
        <tbody>
          {agruparDivergencias(d.divergencias).map(({ grupo, itens: doGrupo }) => (
            <React.Fragment key={grupo}>
              <tr className="border-t border-borda bg-superficie2/70">
                <td colSpan={5} className="px-2 py-1.5">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-texto">
                    {GRUPOS_DIVERGENCIA[grupo].titulo}
                    {doGrupo.length > 1 ? <span className="font-semibold text-textoSuave"> ({doGrupo.length})</span> : null}
                  </p>
                  {['sku_nao_aprovado', 'quantidade', 'preco'].includes(grupo) ? (
                    <p className="text-2xs italic text-textoFraco">{GRUPOS_DIVERGENCIA[grupo].explica}</p>
                  ) : null}
                </td>
              </tr>
          {doGrupo.map((x) => {
            const item = x.nItem != null ? d.itens.find((i) => i.nItem === x.nItem) : undefined;
            let nota: string = '—';
            let oc: string = '—';
            let dif: string = '';
            if (x.regra === 'preco') {
              const vn = item?.valorUnit ?? real(x.veio);
              const vo = real(x.esperado?.replace(/^até\s*/, '')) ?? null;
              const p = precosDaMensagem(x.mensagem);
              const voReal = p ? p.ref : vo;
              nota = vn != null ? `${brl(Number(vn))}/un.` : (x.veio ?? '—');
              oc =
                p?.pre != null
                  ? `Pré-pedido ${p.prePedido}: ${brl(p.pre)}/un.` + (p.oc != null && Math.abs(p.oc - p.pre) > 0.005 ? `\nOC hoje: ${brl(p.oc)}/un. (alterada)` : '')
                  : voReal != null
                    ? `${brl(voReal)}/un.`
                    : (x.esperado ?? '—');
              if (vn != null && voReal) {
                const df = Number(vn) - voReal;
                dif = `${df >= 0 ? '+' : '−'}${brl(Math.abs(df))} (${df >= 0 ? '+' : '−'}${Math.abs((df / voReal) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)`;
              }
            } else if (x.regra === 'quantidade') {
              const qn = real(x.veio) ?? item?.quantidade ?? null;
              const qo = real(x.esperado?.replace(/^até\s*/, ''));
              nota = qn != null ? `${qn} un.` : '—';
              const conta = contaDaQuantidade(x);
              oc = conta
                ? [conta.aprovado != null ? `aprovado ${conta.aprovado} un.` : null, conta.jaRecebido ? `já recebido ${conta.jaRecebido}` : null, `saldo ${conta.saldo} un.`]
                    .filter(Boolean)
                    .join('\n')
                : qo != null
                  ? `saldo ${qo} un.`
                  : (x.esperado ?? '—');
              if (qn != null && qo != null) dif = `${qn - qo >= 0 ? '+' : ''}${qn - qo} un.`;
            } else if (x.regra === 'fora_pre_pedido') {
              // "a OC 43496 tem o produto (30 un), o pré-pedido 9347 (o aprovado) não tem — incluído na OC depois de criada."
              const m = /a OC (\d+) tem o produto \(([\d.,]+) un\), o pré-pedido (\d+)/.exec(x.mensagem);
              nota = item ? `${item.quantidade ?? '—'} un. · ${item.valorUnit != null ? brl(Number(item.valorUnit)) : '—'}` : (x.veio ?? '—');
              oc = m
                ? `OC ${m[1]}: ${m[2]} un.\nPré-pedido ${m[3]}: não tem${x.mensagem.includes('incluído na OC depois') ? '\nincluído na OC depois de criada' : ''}`
                : (x.esperado ?? '—');
              dif = 'fora do aprovado';
            } else if (x.regra === 'item_fora_oc') {
              // A OC da nota não tem o SKU; se outra OC do fornecedor tem, diz qual (xPed errado).
              nota = item ? `${item.quantidade ?? '—'} un. · ${item.valorUnit != null ? brl(Number(item.valorUnit)) : '—'}` : (x.veio ?? '—');
              // Achado só pelo EAN, sem de-para: o SKU pode ser o vínculo errado, não o item.
              const semDePara = (d.deParas ?? []).some((p) => p.nItem === x.nItem);
              oc =
                `OC ${d.nota.pedidoCompra ?? '—'}: não tem` +
                (x.ocSugerida ? `\n→ está na OC ${x.ocSugerida}` : '') +
                (semDePara ? '\nSKU achado só pelo EAN, sem de-para' : '');
              dif = x.ocSugerida ? 'OC errada?' : semDePara ? 'confira o de-para' : 'fora da OC';
            } else if (item) {
              nota = `${item.quantidade ?? '—'} un. · ${item.valorUnit != null ? brl(Number(item.valorUnit)) : '—'}`;
              oc = x.esperado ?? '—';
            } else {
              nota = x.veio ?? '—';
              oc = x.esperado ?? '—';
            }
            return (
              <tr key={x.id} className="border-t border-borda align-top">
                <td className="px-2 py-1.5">
                  <span className="whitespace-nowrap rounded bg-status-divergenciaFundo px-1.5 py-0.5 text-2xs font-semibold text-status-divergencia">
                    {TIPO_DIVERGENCIA[x.regra] ?? x.regra}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  {item ? (
                    <>
                      <span className="font-mono font-semibold">{item.cprod ?? '—'}</span>
                      {item.produto ? <span className="text-textoSuave"> · produto {item.produto}</span> : null}
                      <span className="block max-w-[260px] truncate text-textoFraco" title={item.descricao ?? undefined}>
                        {item.descricao}
                      </span>
                    </>
                  ) : (
                    <span className="text-textoFraco">{x.mensagem}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">{nota}</td>
                {/* Uma informação por linha (pré-pedido, OC hoje...): numa linha só a gaveta ganhava scroll lateral. */}
                <td className="min-w-[150px] whitespace-pre-line px-2 py-1.5 text-right tabular-nums">{oc}</td>
                <td className={cn('whitespace-nowrap px-2 py-1.5 text-right font-semibold tabular-nums', dif.startsWith('+') ? 'text-status-divergencia' : 'text-textoFraco')}>
                  {dif}
                </td>
              </tr>
            );
          })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Chegou no Full (Geisy, 29/10/2026): tudo alocado → transferência do
 * estoque transitório (04) para a venda (01). Divergente (avaria, falta):
 * a quantidade que subiu de cada item e o motivo, que vai para a linha do
 * tempo; o resto fica no 04 e a nota cai em Divergência até alguém decidir.
 * Mover estoque pede um segundo clique com o resumo do que vai e para onde.
 */
function ChegadaNoFull({
  d,
  carregando,
  aoConfirmar,
}: {
  d: DetalheNota;
  carregando: boolean;
  aoConfirmar: (c: { divergente?: boolean; motivo?: string; itens?: { produto: number; quantidade: number }[] }) => void;
}): JSX.Element {
  const n = d.nota;
  const [modo, definirModo] = React.useState<'inicio' | 'tudo' | 'divergente'>('inicio');
  const [motivo, definirMotivo] = React.useState('');
  const [qtds, definirQtds] = React.useState<Record<number, string>>({});
  const itens = useQuery<{ itens: { produto: number; descricao: string | null; quantidade: number; noTransitorio: number }[] }>({
    queryKey: ['entrada-chegada-full', n.chave],
    queryFn: () => api.get(`/api/entradas/nota/${n.chave}/chegada-full/itens`),
  });
  const lista = itens.data?.itens ?? [];
  const qtd = (p: number, padrao: number): number => {
    const v = qtds[p];
    return v == null || v === '' ? padrao : Number(v);
  };
  const total = lista.reduce((s, i) => s + i.quantidade, 0);
  const faltaNo04 = lista.filter((i) => i.noTransitorio < i.quantidade);
  const mexeu = lista.some((i) => qtd(i.produto, i.quantidade) !== i.quantidade);
  const acima = lista.filter((i) => qtd(i.produto, i.quantidade) > Math.min(i.quantidade, i.noTransitorio));
  const vai = lista.reduce((s, i) => s + qtd(i.produto, i.quantidade), 0);
  const aviso04 = faltaNo04.length ? (
    <p className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-xs text-status-atencao">
      {faltaNo04.length} item(ns) com menos no estoque transitório do que a nota trouxe — confira se parte já foi transferida no Procfit.
    </p>
  ) : null;

  if (modo === 'inicio') {
    return (
      <>
        <p className="text-[13px]">Confirme quando a carga chegou e foi alocada no {n.destinoNome}: o estoque passa do transitório (04) para a venda (01).</p>
        {aviso04}
        <Botao variante="primaria" className="w-full" disabled={!lista.length} onClick={() => definirModo('tudo')}>
          <Truck className="h-4 w-4" /> Chegou e alocou tudo no Full
        </Botao>
        <button type="button" className="text-xs font-semibold text-status-divergencia hover:underline" onClick={() => definirModo('divergente')}>
          Chegou com divergência (avaria, falta)?
        </button>
      </>
    );
  }
  if (modo === 'tudo') {
    return (
      <>
        <p className="rounded-lg bg-superficie2 px-3 py-2 text-[13px]">
          <b>{total.toLocaleString('pt-BR')} un.</b> de {lista.length} item(ns) vão do estoque transitório para a venda do {n.destinoNome}. Confirmar?
        </p>
        {aviso04}
        <div className="flex gap-2">
          <Botao variante="fantasma" onClick={() => definirModo('inicio')}>
            Voltar
          </Botao>
          <Botao variante="primaria" className="flex-1" carregando={carregando} disabled={faltaNo04.length > 0} onClick={() => aoConfirmar({})}>
            <Truck className="h-4 w-4" /> Sim, chegou tudo
          </Botao>
        </div>
      </>
    );
  }
  const falta = !mexeu ? 'mude a quantidade de ao menos um item' : !motivo.trim() ? 'diga o que houve' : acima.length ? `${acima.length} item(ns) acima do que há no transitório` : null;
  return (
    <>
      <p className="text-[13px]">Informe quanto de cada item foi alocado no {n.destinoNome}. Só isso vai para a venda; o resto fica no transitório.</p>
      {itens.isLoading ? (
        <Esqueleto className="h-20" />
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-textoFraco">
            <tr>
              <th className="py-1">Produto</th>
              <th className="py-1 text-right">Nota</th>
              <th className="py-1 text-right" title="Saldo hoje no estoque transitório (04)">
                No 04
              </th>
              <th className="py-1 text-right">Alocado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {lista.map((i) => (
              <tr key={i.produto}>
                <td className="py-1">
                  <b className="tabular-nums">{i.produto}</b> <span className="text-textoFraco">{i.descricao}</span>
                </td>
                <td className="py-1 text-right tabular-nums">{i.quantidade}</td>
                <td className={cn('py-1 text-right tabular-nums', i.noTransitorio < i.quantidade && 'font-semibold text-status-atencao')}>{i.noTransitorio}</td>
                <td className="py-1 text-right">
                  <Campo
                    className="ml-auto h-7 w-20 text-right text-xs"
                    inputMode="numeric"
                    value={qtds[i.produto] ?? String(i.quantidade)}
                    onChange={(e) => definirQtds((q) => ({ ...q, [i.produto]: e.target.value.replace(/\D/g, '') }))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Campo placeholder="O que houve? (avaria, falta, caixa amassada...) — vai para a linha do tempo" value={motivo} onChange={(e) => definirMotivo(e.target.value)} />
      <p className="text-2xs text-textoSuave">
        {falta ? `Para confirmar: ${falta}.` : `${vai.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')} un. vão para a venda; o resto fica no transitório e a nota vai para Divergência.`}
      </p>
      <div className="flex gap-2">
        <Botao variante="fantasma" onClick={() => definirModo('inicio')}>
          Voltar
        </Botao>
        <Botao
          variante="primaria"
          className="flex-1"
          disabled={Boolean(falta) || !lista.length}
          carregando={carregando}
          onClick={() => aoConfirmar({ divergente: true, motivo: motivo.trim(), itens: lista.map((i) => ({ produto: i.produto, quantidade: qtd(i.produto, i.quantidade) })) })}
        >
          <Truck className="h-4 w-4" /> Confirmar o que foi alocado
        </Botao>
      </div>
    </>
  );
}
